import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config';
import type { 
  VolunteerProfile, 
  CreateVolunteerProfileParams, 
  UpdateVolunteerProfileParams 
} from '@/types/volunteer';

const VOLUNTEER_PROFILES_COLLECTION = 'volunteerProfiles';

/**
 * Create a volunteer profile
 */
export async function createVolunteerProfile(params: CreateVolunteerProfileParams): Promise<string> {
  try {
    const profileData: any = {
      userId: params.userId,
      fullName: params.fullName,
      fullNameEn: params.fullNameEn,
      fullNameAr: params.fullNameAr,
      email: params.email,
      phone: params.phone,
      nationalId: params.nationalId,
      employmentStatus: params.employmentStatus,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Add optional fields only if they exist
    if (params.scfhsNumber) {
      profileData.scfhsNumber = params.scfhsNumber;
    }
    if (params.volunteerAreas) {
      profileData.volunteerAreas = params.volunteerAreas;
    }

    const docRef = await addDoc(collection(db, VOLUNTEER_PROFILES_COLLECTION), profileData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating volunteer profile:', error);
    throw error;
  }
}

/**
 * Get volunteer profile by user ID
 */
export async function getVolunteerProfileByUserId(userId: string): Promise<VolunteerProfile | null> {
  try {
    const q = query(
      collection(db, VOLUNTEER_PROFILES_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      id: doc.id,
      fullName: data.fullName,
      fullNameEn: data.fullNameEn || data.fullName,
      fullNameAr: data.fullNameAr || '',
      email: data.email,
      phone: data.phone,
      nationalId: data.nationalId,
      scfhsNumber: data.scfhsNumber,
      employmentStatus: data.employmentStatus,
      volunteerAreas: data.volunteerAreas,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  } catch (error) {
    console.error('Error getting volunteer profile:', error);
    throw error;
  }
}

/**
 * Get volunteer profile by profile ID
 */
export async function getVolunteerProfile(profileId: string): Promise<VolunteerProfile | null> {
  try {
    const docRef = doc(db, VOLUNTEER_PROFILES_COLLECTION, profileId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      fullName: data.fullName,
      fullNameEn: data.fullNameEn || data.fullName,
      fullNameAr: data.fullNameAr || '',
      email: data.email,
      phone: data.phone,
      nationalId: data.nationalId,
      scfhsNumber: data.scfhsNumber,
      employmentStatus: data.employmentStatus,
      volunteerAreas: data.volunteerAreas,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  } catch (error) {
    console.error('Error getting volunteer profile:', error);
    throw error;
  }
}

/**
 * Update volunteer profile
 */
export async function updateVolunteerProfile(
  profileId: string,
  updates: UpdateVolunteerProfileParams
): Promise<void> {
  try {
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    if (updates.fullName !== undefined) updateData.fullName = updates.fullName;
    if (updates.fullNameEn !== undefined) updateData.fullNameEn = updates.fullNameEn;
    if (updates.fullNameAr !== undefined) updateData.fullNameAr = updates.fullNameAr;
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.phone !== undefined) updateData.phone = updates.phone;
    if (updates.nationalId !== undefined) updateData.nationalId = updates.nationalId;
    if (updates.scfhsNumber !== undefined) updateData.scfhsNumber = updates.scfhsNumber;
    if (updates.employmentStatus !== undefined) updateData.employmentStatus = updates.employmentStatus;
    if (updates.volunteerAreas !== undefined) updateData.volunteerAreas = updates.volunteerAreas;

    const profileRef = doc(db, VOLUNTEER_PROFILES_COLLECTION, profileId);
    await updateDoc(profileRef, updateData);
  } catch (error) {
    console.error('Error updating volunteer profile:', error);
    throw error;
  }
}

/**
 * Delete volunteer profile
 */
export async function deleteVolunteerProfile(profileId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, VOLUNTEER_PROFILES_COLLECTION, profileId));
  } catch (error) {
    console.error('Error deleting volunteer profile:', error);
    throw error;
  }
}

/**
 * Get all volunteer profiles
 */
export async function getAllVolunteerProfiles(): Promise<VolunteerProfile[]> {
  try {
    const querySnapshot = await getDocs(collection(db, VOLUNTEER_PROFILES_COLLECTION));
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        fullName: data.fullName,
        fullNameEn: data.fullNameEn || data.fullName,
        fullNameAr: data.fullNameAr || '',
        email: data.email,
        phone: data.phone,
        nationalId: data.nationalId,
        scfhsNumber: data.scfhsNumber,
        employmentStatus: data.employmentStatus,
        volunteerAreas: data.volunteerAreas,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    });
  } catch (error) {
    console.error('Error getting all volunteer profiles:', error);
    throw error;
  }
}

